import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, CircleDot, Loader2, RotateCcw, Square, TriangleAlert } from "lucide-react";
import { formatElapsed } from "@/lib/ui";
import { useRecorder } from "@/recorder/context";
import RecordSplitButton from "./RecordSplitButton";
import { ProgressRing } from "./UploadProgress";

const chip = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.95 },
  transition: { duration: 0.15 },
} as const;

export default function HeaderRecorder() {
  const recorder = useRecorder();
  const [busy, setBusy] = useState(false);

  const isCapturing = recorder.status === "recording";
  const isDraining =
    recorder.status === "uploading" || recorder.status === "finalizing";
  const isStored = recorder.status === "done";
  const isError = recorder.status === "error";
  const isRequesting = recorder.status === "requesting";
  const upload = recorder.mode === "upload" ? recorder.upload : null;
  const isExtracting =
    isRequesting && (upload?.phase === "extracting" || upload?.phase === "joining");

  async function guard(action: () => Promise<void>) {
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  }

  const handleStart = () => {
    void guard(async () => {
      if (recorder.status === "done" || recorder.status === "error") {
        recorder.reset();
      }
      await recorder.start();
    });
  };

  const handleFiles = (files: File[]) => {
    void guard(() => recorder.uploadFiles(files));
  };

  return (
    <div className="flex h-7 items-center">
      <AnimatePresence mode="wait" initial={false}>
        {isCapturing && (
          <motion.div
            key="capturing"
            {...chip}
            className="box-border flex h-7 items-center gap-2 rounded-xl border border-[#d1d1cf] bg-[#fdfdfc] px-2 text-xs shadow-xs dark:border-[#2a2a28] dark:bg-[#141413]"
          >
            <div className="flex items-center gap-1.5 font-mono text-xs font-medium text-[#EF4444]">
              <CircleDot className="h-3.5 w-3.5 shrink-0 animate-pulse" />
              <span>{formatElapsed(recorder.elapsedMs)}</span>
            </div>

            <div className="flex items-center gap-1">
              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.96 }}
                disabled={busy}
                onClick={() => void guard(() => recorder.stop())}
                className="flex h-5 cursor-pointer items-center gap-1 rounded-md bg-[#222] px-2 text-[11px] font-semibold text-[#fdfdfc] shadow-xs transition-all hover:bg-[#111] dark:bg-[#ededeb] dark:text-[#141413] dark:hover:bg-white"
              >
                <Square className="h-2.5 w-2.5 fill-current" />
                <span>Stop</span>
              </motion.button>

              <motion.button
                type="button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.96 }}
                disabled={busy}
                onClick={() => void guard(() => recorder.cancel())}
                className="flex h-5 cursor-pointer items-center rounded-md border border-[#d1d1cf] bg-[#fdfdfc] px-1.5 text-[11px] font-medium text-neutral-600 transition-all hover:border-neutral-400 hover:bg-[#fafaf8] dark:border-[#2a2a28] dark:bg-[#141413] dark:text-neutral-300 dark:hover:bg-[#1a1a19]"
              >
                Discard
              </motion.button>
            </div>
          </motion.div>
        )}

        {isDraining && (
          <motion.div
            key="draining"
            {...chip}
            className="box-border flex h-7 items-center gap-2 rounded-xl border border-[#d1d1cf] bg-[#fdfdfc] px-2 text-xs shadow-xs dark:border-[#2a2a28] dark:bg-[#141413]"
          >
            {upload !== null && recorder.status !== "finalizing" && upload.progress < 1 ? (
              <div
                className="flex min-w-0 items-center gap-1.5 font-mono text-xs text-[#222] dark:text-[#ededeb]"
                title={upload.fileName}
              >
                <ProgressRing value={upload.progress} className="h-3.5 w-3.5 shrink-0" />
                <span className="tabular-nums">{Math.floor(upload.progress * 100)}%</span>
                {upload.part !== null && (
                  <span className="text-neutral-500 tabular-nums">
                    {upload.part.index + 1}/{upload.part.count}
                  </span>
                )}
                <span className="hidden max-w-[120px] truncate text-neutral-500 sm:inline">
                  {upload.fileName}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 font-mono text-xs text-neutral-500">
                <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-neutral-400" />
                <span>
                  {recorder.status === "finalizing" || upload !== null
                    ? "Finalizing…"
                    : `Uploading (${recorder.pending})`}
                </span>
              </div>
            )}
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.96 }}
              disabled={busy}
              onClick={() => void guard(() => recorder.cancel())}
              className="flex h-5 cursor-pointer items-center rounded-md border border-[#d1d1cf] bg-[#fdfdfc] px-1.5 text-[11px] font-medium text-neutral-600 transition-all hover:bg-[#fafaf8] dark:border-[#2a2a28] dark:bg-[#141413] dark:text-neutral-300 dark:hover:bg-[#1a1a19]"
            >
              {upload !== null ? "Cancel" : "Discard"}
            </motion.button>
          </motion.div>
        )}

        {isStored && (
          <motion.div
            key="stored"
            {...chip}
            className="box-border flex h-7 items-center gap-1.5 rounded-xl border border-[#d1d1cf] bg-[#fdfdfc] px-2 text-xs shadow-xs dark:border-[#2a2a28] dark:bg-[#141413]"
          >
            <div className="flex min-w-0 items-center gap-1 font-mono text-xs text-[#22C55E]">
              <motion.span
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 500, damping: 22 }}
                className="flex shrink-0 items-center"
              >
                <Check className="h-3.5 w-3.5 stroke-[2.5]" />
              </motion.span>
              {upload !== null ? (
                <>
                  <span>uploaded</span>
                  <span
                    className="hidden max-w-[120px] truncate text-neutral-500 sm:inline"
                    title={upload.fileName}
                  >
                    · {upload.fileName}
                  </span>
                </>
              ) : (
                <>
                  <span>{formatElapsed(recorder.elapsedMs)}</span>
                  <span className="hidden text-neutral-500 sm:inline">· stored</span>
                </>
              )}
            </div>
            <div className="ml-1 flex items-center gap-1">
              {upload === null && (
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.96 }}
                  disabled={busy}
                  onClick={handleStart}
                  className="flex h-5 cursor-pointer items-center gap-1 rounded-md border border-[#d1d1cf] bg-[#fdfdfc] px-1.5 text-[11px] font-medium text-neutral-700 transition-all hover:bg-[#fafaf8] dark:border-[#2a2a28] dark:bg-[#141413] dark:text-neutral-300 dark:hover:bg-[#1a1a19]"
                >
                  <RotateCcw className="h-3 w-3 text-neutral-400" />
                  <span className="hidden sm:inline">Record again</span>
                </motion.button>
              )}
              <button
                type="button"
                disabled={busy}
                onClick={() => recorder.reset()}
                className="cursor-pointer font-mono text-[11px] text-neutral-500 transition-colors hover:text-neutral-800 dark:hover:text-neutral-200"
              >
                {upload !== null ? "Done" : "Reset"}
              </button>
            </div>
          </motion.div>
        )}

        {isError && (
          <motion.div
            key="error"
            {...chip}
            className="box-border flex h-7 items-center gap-1.5 rounded-xl border border-red-300 bg-red-50/50 px-2 text-xs shadow-xs dark:border-red-900/50 dark:bg-red-950/20"
          >
            <TriangleAlert className="h-3.5 w-3.5 shrink-0 text-red-600 dark:text-red-400" />
            <span
              className="max-w-[140px] truncate text-xs text-red-600 sm:max-w-[200px] dark:text-red-400"
              title={recorder.error ?? "Recording error"}
            >
              {recorder.error ?? "Recording error"}
            </span>
            <button
              type="button"
              onClick={() => recorder.reset()}
              className="cursor-pointer font-mono text-[11px] text-neutral-500 transition-colors hover:text-neutral-800 dark:hover:text-neutral-200"
            >
              Reset
            </button>
          </motion.div>
        )}

        {isExtracting && upload !== null && (
          <motion.div
            key="extracting"
            {...chip}
            className="box-border flex h-7 items-center gap-2 rounded-xl border border-[#d1d1cf] bg-[#fdfdfc] px-2 text-xs shadow-xs dark:border-[#2a2a28] dark:bg-[#141413]"
          >
            <div
              className="flex min-w-0 items-center gap-1.5 font-mono text-xs text-[#222] dark:text-[#ededeb]"
              title={
                upload.phase === "joining"
                  ? `Joining into ${upload.fileName}`
                  : `Taking the audio out of ${upload.fileName}`
              }
            >
              <ProgressRing value={upload.progress} className="h-3.5 w-3.5 shrink-0" />
              <span className="tabular-nums">{Math.floor(upload.progress * 100)}%</span>
              <span className="hidden text-neutral-500 sm:inline">
                {upload.phase === "joining" ? "joining files" : "extracting audio"}
              </span>
            </div>
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => void recorder.cancel()}
              className="flex h-5 cursor-pointer items-center rounded-md border border-[#d1d1cf] bg-[#fdfdfc] px-1.5 text-[11px] font-medium text-neutral-600 transition-all hover:bg-[#fafaf8] dark:border-[#2a2a28] dark:bg-[#141413] dark:text-neutral-300 dark:hover:bg-[#1a1a19]"
            >
              Cancel
            </motion.button>
          </motion.div>
        )}

        {!isCapturing && !isDraining && !isStored && !isError && !isExtracting && (
          <motion.div key="idle" {...chip} className="relative">
            <RecordSplitButton
              size="sm"
              disabled={busy}
              starting={isRequesting}
              onRecord={handleStart}
              onFiles={handleFiles}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
